import { MdsIcon, MdsIconKind } from "@/design-system/components/icon";
import { useAppStore } from "@/store";
import { observer } from "mobx-react-lite";
import styled from "@emotion/styled";
import {
  AppStoreAudioCaptureStore,
  RecordingStatus,
} from "@/store/audio-capture/AppStoreAudioCaptureStore";
import { noop } from "lodash-es";
import { useState, useRef, useEffect, useLayoutEffect, useCallback, useMemo } from "react";
import { AnimatePresence, motion } from "motion/react";
import { DeleteRecordingModal } from "@/components/modal/delete-recording/delete-recording-modal";
import { AutoStopRecordingModal } from "@/components/modal/auto-stop-recording/auto-stop-recording-modal";
import { isMemAccount } from "@/store/contacts/isMemAccount";
import {
  MDSBubble,
  MDSBubbleAlignment,
  MDSBubbleVariant,
  MdsLink,
  MDSButton,
  MDSButtonVariant,
  MDSIconButton,
  MDSTooltip,
  MDSTooltipPlacement,
  MDSTooltipTrigger,
  MDSText,
  MDSTextVariant,
  MDSIconButtonSize,
  MDSTextColor,
} from "@/mds";
import { useDebounceCallback } from "usehooks-ts";
import { clientEnvModule } from "@/modules/client-env";
import { isWin } from "@/domains/platform/isWin";
import { windowModule } from "@/modules/window";
import { APP_CONFIG } from "@/domains/global/config";
import { FadeBottom } from "@/components/horizontal-scroll/FadeBottom";
import {
  MdsDropdownContentList,
  MdsDropdownIconButton,
  MdsDropdownItemKind,
} from "@/design-system/components/dropdown";
import { electronModule } from "@/modules/electron";
import { logger } from "@/modules/logger";
import { objectModule } from "@/modules/object";
import { AnimationTiming } from "@/design-system/constants/common";
import { ZIndex } from "@/domains/design/constants";
import { css, cx } from "@/domains/emotion";

interface AudioCaptureActionsProps {
  noteId: string;
  onRequestClose?: () => void;
}

export const AudioCaptureActions = observer(function AudioCaptureActions({
  noteId,
  onRequestClose,
}: AudioCaptureActionsProps) {
  const { store } = useAppStore();
  const isAdmin = isMemAccount(store.account.myAccount);

  const [isTextTranscript, setIsTextTranscript] = useState(false);

  const maxHistoryBars = 30;
  const [historyBarHeights, setHistoryBarHeights] = useState<number[]>(
    Array.from({ length: maxHistoryBars }, () => 0.0)
  );

  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const handleDelete = useCallback(() => {
    store.audioCapture.clearRecording();
    setShowDeleteModal(false);
    onRequestClose?.();
  }, [store.audioCapture, onRequestClose]);

  const handleDeleteModalClose = useCallback(() => {
    setShowDeleteModal(false);
    onRequestClose?.();
  }, [onRequestClose]);

  const handleExitClick = useCallback(async () => {
    if (store.audioCapture.status === RecordingStatus.RECORDING) {
      await store.audioCapture.pauseRecording();
    }

    setShowDeleteModal(true);
  }, [store.audioCapture]);

  const handleManageSystemAudioSettings = useCallback(async () => {
    try {
      await electronModule.openSystemSoundSettings?.({ focus: "input" });
    } catch (error) {
      logger.warn({
        message: "[AudioCaptureActions] openSystemSoundSettings failed",
        info: { error: objectModule.safeErrorAsJson(error as Error) },
      });
    }
  }, []);

  const dropdownContentList: MdsDropdownContentList = useMemo(() => {
    const items: MdsDropdownContentList["items"] = [];

    if (store.audioCapture.isDesktop) {
      items.push({
        id: "manage-system-audio",
        kind: MdsDropdownItemKind.Button,
        label: "Manage system audio settings",
        onClick: () => {
          void handleManageSystemAudioSettings();
        },
      });
    }

    return { items };
  }, [handleManageSystemAudioSettings, store.audioCapture.isDesktop]);

  const shouldShowDropdown = useMemo(() => {
    return dropdownContentList.items.length > 0;
  }, [dropdownContentList.items.length]);

  useEffect(() => {
    const visualizer = store.audioCapture.visualizer;
    if (!visualizer) return;

    const timeDomainData = new Uint8Array(visualizer.fftSize);

    let frameCounter = 0;

    let animationFrameId: number;
    const animate = () => {
      animationFrameId = requestAnimationFrame(animate);

      // Only update every 12th frame
      frameCounter++;
      if (frameCounter % 12 !== 0) return;

      const isRecording = store.audioCapture.status === RecordingStatus.RECORDING;

      visualizer.getByteTimeDomainData(timeDomainData);

      if (isRecording) {
        // Calculate RMS for history bars
        let rms = 0;
        for (let i = 0; i < timeDomainData.length; i++) {
          const sample = (timeDomainData[i] - 128) / 128.0;
          rms += sample * sample;
        }
        rms = Math.sqrt(rms / timeDomainData.length);

        // Calculate bar height with logarithmic scaling for better visualization of lower volumes
        let barHeight = Math.log10(rms * 65 + 1) / Math.log10(66); // This maps 0-1 to 0-1 with logarithmic scaling, boosting lower values by 50% more
        barHeight = Math.max(barHeight, 0.1);
        barHeight = Math.min(barHeight, 1);

        setHistoryBarHeights(prev => {
          const newHeights = [...prev, barHeight];
          if (newHeights.length > maxHistoryBars) {
            return newHeights.slice(-maxHistoryBars);
          }
          return newHeights;
        });
      }
    };
    animate();
    return () => {
      cancelAnimationFrame(animationFrameId);
    };
  }, [store.audioCapture.visualizer, store.audioCapture.status]);

  const durationText = store.audioCapture.formattedDuration;
  const autoStopModalVisible = store.audioCapture.autoStopModalVisible;
  const autoStopDialog = store.audioCapture.autoStopDialogContent;
  const recordingStatus = store.audioCapture.status;
  const currentMicrophoneLabel = store.audioCapture.activeAudioInputDeviceLabel;
  const fullTranscript = store.audioCapture.fullTranscript;
  const transcript = store.audioCapture.transcript;
  const hasTranscript = transcript.length > 0;
  const transcriptPaused = store.audioCapture.isTranscriptionPaused;
  const isRecordingWithoutTranscription = store.audioCapture.isRecordingWithoutTranscription;

  const shouldDisplayRetryButtonLabel = !!store.audioCapture.error;

  // We use this to delay the rendering of the inner components until the animation is complete
  const [showInnerComponents, setShowInnerComponents] = useState(false);

  // We use this to scroll to the bottom to the transcript as it is appended to
  const transcriptContainerRef = useRef<HTMLDivElement>(null);
  const isAutoScrollEnabledRef = useRef(true);
  const handleTranscriptScroll = useDebounceCallback(() => {
    if (!transcriptContainerRef.current) return;
    const { scrollTop, clientHeight, scrollHeight } = transcriptContainerRef.current;
    const isCloseToBottom = scrollTop + clientHeight >= scrollHeight - 10;
    isAutoScrollEnabledRef.current = isCloseToBottom;
  }, 30);

  useLayoutEffect(() => {
    // Check all conditions that might make the transcript container not exist yet.
    if (
      isAutoScrollEnabledRef.current &&
      hasTranscript &&
      showInnerComponents &&
      transcriptContainerRef.current
    ) {
      transcriptContainerRef.current.scrollTop = transcriptContainerRef.current.scrollHeight;
    }
  }, [fullTranscript, hasTranscript, showInnerComponents]);

  return (
    <>
      {autoStopDialog && (
        <AutoStopRecordingModal
          isOpen={autoStopModalVisible}
          onClose={() => store.audioCapture.setAutoStopModalVisible(false)}
          title={autoStopDialog.title}
          message={autoStopDialog.message}
        />
      )}
      <DeleteRecordingModal
        isOpen={showDeleteModal}
        onClose={handleDeleteModalClose}
        onDelete={handleDelete}
      />
      {!showDeleteModal && (
        <StyledContainer
          initial={{ height: 74 }}
          animate={{ height: "auto" }}
          transition={{ duration: 0.3, ease: [0.4, 0.0, 0.2, 1] }}
          onAnimationComplete={() => {
            setShowInnerComponents(true);
          }}
        >
          {showInnerComponents && (
            <motion.div
              initial={{ opacity: 0, y: 20, width: "100%", height: "100%", position: "relative" }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, ease: [0.4, 0.0, 0.2, 1] }}
            >
              <StyledHeader>
                <StyledHeaderTop>
                  <MDSTooltip
                    config={{
                      placement: MDSTooltipPlacement.Top,
                      label:
                        recordingStatus !== RecordingStatus.RECORDING
                          ? "Not recording"
                          : currentMicrophoneLabel
                            ? `Recording from device "${currentMicrophoneLabel}"`
                            : "No microphone found",
                    }}
                  >
                    <MDSTooltipTrigger>
                      <StyledHeaderTitle variant={MDSTextVariant.TextM}>
                        Voice Recording
                      </StyledHeaderTitle>
                    </MDSTooltipTrigger>
                  </MDSTooltip>
                  <RightSide>
                    {shouldShowDropdown && (
                      <MdsDropdownIconButton
                        iconKind={MdsIconKind.GearCode}
                        contentList={dropdownContentList}
                        size={MDSIconButtonSize.Medium}
                        placement="below"
                        tooltipConfig={{
                          label: "More",
                          placement: MDSTooltipPlacement.Top,
                          delaySeconds: AnimationTiming.TooltipHoverDelaySeconds,
                        }}
                        innerStyles={{
                          ContentContainer: {
                            className: cx(
                              "voice-actions-dropdown",
                              css({ zIndex: ZIndex.FloatingActionsDropdown })
                            ),
                          },
                        }}
                      />
                    )}
                    <MDSTooltip
                      config={{
                        label: "Delete recording",
                        placement: MDSTooltipPlacement.Top,
                        delaySeconds: AnimationTiming.TooltipHoverDelaySeconds,
                      }}
                    >
                      <CancelRecordingButton
                        onClick={handleExitClick}
                        iconKind={MdsIconKind.Exit}
                      />
                    </MDSTooltip>
                  </RightSide>
                </StyledHeaderTop>
              </StyledHeader>
              {hasTranscript && (
                <StyledTranscriptContainer
                  ref={transcriptContainerRef}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2, ease: [0.4, 0.0, 0.2, 1] }}
                  onScroll={handleTranscriptScroll}
                >
                  <StyledTranscriptText
                    onClick={e => {
                      if (isAdmin && e.altKey) setIsTextTranscript(!isTextTranscript);
                    }}
                  >
                    {isTextTranscript && <span>{transcript}</span>}

                    {!isTextTranscript &&
                      fullTranscript.map(t => (
                        <StyledBubble
                          key={t.sortKey}
                          align={
                            t.side < 0
                              ? MDSBubbleAlignment.Center
                              : t.side > 0
                                ? MDSBubbleAlignment.Left
                                : MDSBubbleAlignment.Right
                          }
                          variant={
                            t.side < 0
                              ? MDSBubbleVariant.Transparent
                              : t.side === 0
                                ? MDSBubbleVariant.VoiceSelf
                                : MDSBubbleVariant.VoiceOthers
                          }
                          isLastInGroup={t.isLastOfBlock}
                        >
                          {t.transcript}
                        </StyledBubble>
                      ))}
                    {transcriptPaused && (
                      <TranscriptStatus>
                        <TranscriptStatusIcon kind={MdsIconKind.CloudSlash} />
                        <span>Transcription paused, will resume automatically</span>
                      </TranscriptStatus>
                    )}
                    <ReportAudioIssues />
                  </StyledTranscriptText>
                  <DownloadDesktopAppNoticeOnWeb />
                </StyledTranscriptContainer>
              )}
              {!hasTranscript &&
                (isRecordingWithoutTranscription ||
                  [RecordingStatus.INITIALIZING, RecordingStatus.RECORDING].includes(
                    recordingStatus
                  )) && (
                  <StyledTranscriptContainer>
                    {isRecordingWithoutTranscription && (
                      <TranscriptStatus>
                        <TranscriptNotSupportedStatusIcon kind={MdsIconKind.TextSlash} />
                        <RedTextColor>Transcription not supported on this application</RedTextColor>
                      </TranscriptStatus>
                    )}
                    {[RecordingStatus.INITIALIZING, RecordingStatus.RECORDING].includes(
                      recordingStatus
                    ) && (
                      <TranscriptStatus>
                        <TranscriptStatusIcon kind={MdsIconKind.Microphone} />
                        <span>
                          {recordingStatus === RecordingStatus.INITIALIZING
                            ? "Initializing..."
                            : "Listening..."}
                        </span>
                      </TranscriptStatus>
                    )}
                    <ReportAudioIssues />
                    <DownloadDesktopAppNoticeOnWeb />
                  </StyledTranscriptContainer>
                )}
              <FadeBottom $surfaceKind="modal" />
            </motion.div>
          )}
          <StyledButtonsContainer $showSeparator={showInnerComponents}>
            {recordingStatus === RecordingStatus.RECORDING && (
              <StyledIconButton
                onClick={() => store.audioCapture.pauseRecording()}
                isDisabled={store.audioCapture.isMicrophoneDisconnected}
                size={MDSIconButtonSize.Medium}
                iconKind={MdsIconKind.PauseSolid}
              />
            )}
            {recordingStatus === RecordingStatus.PAUSED && (
              <StyledIconButton
                onClick={() => store.audioCapture.resumeRecording()}
                isDisabled={store.audioCapture.isMicrophoneDisconnected}
                size={MDSIconButtonSize.Medium}
                iconKind={MdsIconKind.MicrophoneAlt}
              />
            )}
            {recordingStatus === RecordingStatus.LOCKED && (
              <StyledIconButton
                iconKind={MdsIconKind.MicrophoneAlt}
                size={MDSIconButtonSize.Medium}
                onClick={() => {
                  // Locked state means we haven't initialized or started recording yet,
                  // but we display resume to the user.
                  store.audioCapture.startRecording({ noteId });
                }}
                isDisabled={
                  isFinishedRecording(recordingStatus) ||
                  store.audioCapture.isMicrophoneDisconnected
                }
              />
            )}
            <HistoryBarsContainer>
              {historyBarHeights.map((height, index) => (
                <HistoryBar
                  key={index}
                  style={{
                    height: `${height * 100}%`,
                    transform: `translateX(${(index / maxHistoryBars) * 100}%) translateY(-50%)`,
                    left: `${(index / maxHistoryBars) * 100}%`,
                  }}
                />
              ))}
            </HistoryBarsContainer>
            <StyledDurationText variant={MDSTextVariant.TextSm} color={MDSTextColor.Emphasis}>
              {durationText}
            </StyledDurationText>
            {isProcessing(recordingStatus) && (
              <MDSButton variant={MDSButtonVariant.Chrome} onClick={noop}>
                {getProcessingLabel(recordingStatus, store.audioCapture.uploadProgressPercent)}
              </MDSButton>
            )}
            {isFinishedRecording(recordingStatus) && !isProcessing(recordingStatus) && (
              <MDSButton
                variant={MDSButtonVariant.Chrome}
                onClick={() => store.audioCapture.clearRecording()}
              >
                Cancel
              </MDSButton>
            )}
            <MDSTooltip
              config={
                !store.audioCapture.activeNoteObservable?.existsRemotely
                  ? {
                      label: "Cannot process voice recording until note is synced.",
                      placement: MDSTooltipPlacement.Top,
                    }
                  : undefined
              }
            >
              <MDSButton
                variant={MDSButtonVariant.Contrast}
                onClick={async () => {
                  if (
                    [
                      RecordingStatus.RECORDING,
                      RecordingStatus.PAUSED,
                      RecordingStatus.STOPPING,
                    ].includes(recordingStatus)
                  ) {
                    await store.audioCapture.stopRecording();
                  }
                  await store.audioCapture.processRecording();
                }}
                isDisabled={!canPressDone(store.audioCapture)}
              >
                {shouldDisplayRetryButtonLabel ? "Retry" : "Done"}
              </MDSButton>
            </MDSTooltip>
          </StyledButtonsContainer>
        </StyledContainer>
      )}
    </>
  );
});

const canPressDone = (store: AppStoreAudioCaptureStore): boolean => {
  if (store.isMicrophoneDisconnected || !store.activeNoteObservable?.existsRemotely) return false;

  const status = store.status;
  if (status === RecordingStatus.LOCKED) return true;
  if (status === RecordingStatus.PAUSED) return true;
  if (status === RecordingStatus.RECORDING) return true;
  if (status === RecordingStatus.STOPPED) return true;
  if (status === RecordingStatus.UPLOADED) return true;
  return false;
};

const isFinishedRecording = (status: RecordingStatus): boolean => {
  if (status === RecordingStatus.STOPPED) return true;
  if (status === RecordingStatus.UPLOADING) return true;
  if (status === RecordingStatus.UPLOADED) return true;
  if (status === RecordingStatus.PROCESSING) return true;
  return false;
};

const isProcessing = (status: RecordingStatus): boolean => {
  if (status === RecordingStatus.PROCESSING) return true;
  if (status === RecordingStatus.UPLOADING) return true;
  return false;
};

function getProcessingLabel(status: RecordingStatus, progressPercent: number | null): string {
  if (status === RecordingStatus.UPLOADING) {
    if (typeof progressPercent === "number" && Number.isFinite(progressPercent)) {
      const clamped = Math.min(100, Math.max(0, Math.round(progressPercent)));
      return `Uploading... (${clamped}%)`;
    }
    return "Uploading...";
  }

  if (status === RecordingStatus.PROCESSING) {
    return "Processing...";
  }

  return "Processing...";
}

const StyledContainer = styled(motion.div)`
  display: flex;
  flex-direction: column;
  padding: ${({ theme }) => theme.spacing[4]};
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing[2]};
  background-color: ${({ theme }) => theme.colors.surface.modal};
  border: 1px solid ${({ theme }) => theme.colors.border.surface};
  border-radius: ${({ theme }) => theme.borderRadius.xlarge};
  box-shadow: ${({ theme }) => theme.shadows.surface};
  width: 415px;
`;

const StyledButtonsContainer = styled.div<{ $showSeparator?: boolean }>`
  display: flex;
  flex-direction: row;
  width: 100%;
  align-items: center;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing.sm};

  ${({ theme, $showSeparator }) =>
    $showSeparator &&
    `
  padding-top: ${theme.spacing[4]};
  border-top: 1px solid ${theme.colors.border.default}`};
`;

const StyledDurationText = styled(MDSText)`
  font-weight: ${({ theme }) => theme.fontWeights.semiBold};
  text-align: right;
  padding-right: ${({ theme }) => theme.spacing[4]};
`;

const StyledTranscriptContainer = styled(motion.div)`
  padding-bottom: ${({ theme }) => theme.spacing.md};
  overflow-y: auto;
  max-height: max(100px, 50vh);
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm};
  scrollbar-width: none;
`;

const StyledTranscriptText = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs};
  font-size: ${({ theme }) => theme.fontSizes.small};
  color: ${({ theme }) => theme.colors.grey.x700};
  line-height: ${({ theme }) => theme.lineHeights.smedium};
  white-space: pre-wrap;
`;

const TranscriptStatus = styled.div`
  align-items: flex-start;
  color: ${({ theme }) => theme.colors.grey.x500};
  display: flex;
  flex-direction: row;
  font-size: ${({ theme }) => theme.fontSizes.xsmall};
  gap: ${({ theme }) => theme.spacing.xs};
  padding-left: ${({ theme }) => theme.spacing.sm};
  line-height: ${({ theme }) => theme.lineHeights.xsmall};
  width: 100%;
`;

const TranscriptStatusIcon = styled(MdsIcon)`
  font-size: ${({ theme }) => theme.fontSizes.xsmall};
  height: 16px;
  width: 16px;

  & svg {
    color: ${({ theme }) => theme.colors.grey.x500};
  }
`;

const TranscriptNotSupportedStatusIcon = styled(MdsIcon)`
  font-size: ${({ theme }) => theme.fontSizes.xsmall};
  height: 16px;
  width: 16px;

  & svg {
    color: ${({ theme }) => theme.colors.red.x500};
  }
`;

const RedTextColor = styled.span`
  color: ${({ theme }) => theme.colors.red.x500};
`;

const StyledHeader = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  padding-bottom: ${({ theme }) => theme.spacing[2]};
  justify-content: space-between;
`;

const StyledHeaderTitle = styled(MDSText)``;

const StyledHeaderTop = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding-left: ${({ theme }) => theme.spacing[4]};
  padding-right: ${({ theme }) => theme.spacing[1]};
  padding-top: ${({ theme }) => theme.spacing[1]};
  padding-bottom: ${({ theme }) => theme.spacing[2]};
`;

const HistoryBarsContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  flex: 1;
  min-width: 0;
  padding: 0 4px;
  position: relative;
  overflow: hidden;
  height: 40px;
`;

const HistoryBar = styled.div`
  border-radius: 2px;
  width: 1.35%;
  margin: 0 0.025%;
  background-color: ${({ theme }) => theme.colors.icon.emphasis};
  transition: transform 0.2s ease-in-out;
  position: absolute;
  top: 50%;
  transform: translateX(0) translateY(-50%);
  will-change: transform;
`;

const DownloadDesktopAppNoticeOnWebContainer = styled.div`
  padding: ${({ theme }) => `${theme.spacing.sm} ${theme.spacing.md} ${theme.spacing.sm} 0`};
`;

const DownloadDesktopAppNoticeOnWeb = observer(function DownloadDesktopAppNoticeOnWeb() {
  if (clientEnvModule.isDesktop()) return null;

  return (
    <DownloadDesktopAppNoticeOnWebContainer>
      <TranscriptStatus>
        <TranscriptStatusIcon kind={MdsIconKind.Warning} />
        <span>
          Web only transcribes what your microphone hears.{" "}
          <MdsLink
            onPress={() => {
              windowModule.openInNewTab({
                url: new URL(isWin() ? APP_CONFIG.URLS.WINDOWS_APP : APP_CONFIG.URLS.MAC_APP),
              });
            }}
          >
            Download the Mem desktop app
          </MdsLink>{" "}
          to transcribe all system audio and capture complete meeting notes.
        </span>
      </TranscriptStatus>
    </DownloadDesktopAppNoticeOnWebContainer>
  );
});

const ReportAudioIssues = observer(function ReportAudioIssues() {
  const { store } = useAppStore();

  return (
    <>
      <AnimatePresence>
        {store.audioCapture.isAudioSilent && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{
              height: 0,
              opacity: 0,
              transition: { duration: 0.2, ease: "easeInOut" },
            }}
            transition={{ duration: 0.2, ease: "easeInOut" }}
          >
            <TranscriptStatus>
              <RedTextColor>No audio detected, please check your microphone.</RedTextColor>
            </TranscriptStatus>
          </motion.div>
        )}
      </AnimatePresence>
      <AnimatePresence>
        {store.audioCapture.isMicrophoneDisconnected && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{
              height: 0,
              opacity: 0,
              transition: { delay: 0.8, duration: 0.2, ease: "easeInOut" },
            }}
            transition={{ duration: 0.2, ease: "easeInOut" }}
          >
            <TranscriptStatus>
              <TranscriptStatusIcon kind={MdsIconKind.MicrophoneSlash} />
              <span>Microphone was disconnected, restarting voice mode...</span>
            </TranscriptStatus>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
});

const StyledBubble = styled(MDSBubble)`
  & > * {
    max-width: unset;

    ${({ align, theme }) =>
      align === MDSBubbleAlignment.Left
        ? `margin-right: ${theme.spacing[5]};`
        : `margin-left: ${theme.spacing[5]};`};
  }
`;

const StyledIconButton = styled(MDSIconButton)`
  & svg {
    font-size: 18px;
  }
`;

const RightSide = styled.div`
  min-height: 40px;
  display: flex;
  flex-direction: row;
  gap: ${({ theme }) => theme.spacing.xs};
`;

const CancelRecordingButton = styled(MDSIconButton)`
  min-width: 40px;
`;
